import {
  SimpleForm,
  NumberInput,
  required,
  useRecordContext,
  useCreate,
  useNotify,
  Loading,
  useGetList,
  Toolbar,
  SaveButton,
  useTranslate,
  useUpdate,
  DateInput,
  TimeInput,
  TextInput,
  SelectInput,
  useGetOne,
  useStore,
  FunctionField,
} from 'react-admin'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Box,
  Alert,
  AlertTitle,
  Typography,
} from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import { useState, useCallback, useEffect, useMemo } from 'react'
import moment from 'moment'
import 'moment/locale/th'
import _ from 'lodash'
import BasePageLayout from '../../layouts/BasePageLayout'
import { ServiceMultiSelectInput } from '../../components/form/ServiceMultiSelectInput'
import { Calendar } from '../../components/BigCalendar'
import { EventType, STATUS_COLOR } from './type'
import { branchConfigTimeConverter } from '../../utils/times'
import { BranchSelect } from '../../components/app-bar/type'
import { BOOKINGVIEW_FIELDS as BOOKING_FIELDS } from '../../providers/localization/constant'

import axios from '../../config/connections'
import LaneSelectButton from './LaneSelectButton'
import BookingSelectBranch from './BranchSelectInput'
import { timeToId } from './utils'
import { useUserContextProvider } from '../../contexts/user-context/useUserContextProvider'
import { RolePermission } from '../roles-permissions/type'
import { CancelReasonInput } from '../../components/domains/bookings/CancelReasonInput'
import { useFormContext } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

const BookingResource: React.FC = () => {
  const [searchParams] = useSearchParams()
  const dateQuery = useMemo(() => searchParams.get('date'), [searchParams])
  window.history.pushState({}, document.title, window.location.pathname)
  const notify = useNotify()
  const t = useTranslate()
  const record = useRecordContext()
  // Page State
  const [globalBranch] = useStore<BranchSelect>('branch.selected')
  const [selectedBranch, setSelectedBranch] = useState(globalBranch?.id)
  const [selectedLane, setSelectLane] = useState(1)
  const { currentUserData, isLoading: isUserLoading, error: errorUser } = useUserContextProvider()
  const bookingManagementPermissions = useMemo(
    () =>
      _.get(currentUserData, 'role.permissions', []).find(
        (permission: RolePermission) => permission.key === 'booking_management',
      ),
    [currentUserData],
  )
  const bookingBookManagementPermission = useMemo(
    () =>
      bookingManagementPermissions?.sub_menu?.find(
        (permission: RolePermission) => permission.key === 'booking_book_management',
      ),
    [bookingManagementPermissions?.sub_menu],
  )
  const [bookingPermission, setBookingPermission] = useState<RolePermission>(
    bookingBookManagementPermission,
  )

  // Calendar State
  const [view, setView] = useState('day')
  const [selectedDate, setSelectDate] = useState(
    dateQuery ? moment(dateQuery).toDate() : new Date(),
  )
  const [start, setStart] = useState(null)
  const [end, setEnd] = useState(null)
  const [overlap, setOverlap] = useState(false)
  const [events, setEvents] = useState<EventType[]>([])
  const [selectedEvent, setSelectedEvent] = useState(null)

  // Dialog State
  const [actionOpen, setActionOpen] = useState(false)

  // Booking Form State
  const [bookingOpen, setBookingOpen] = useState(false)
  const [formAction, setFormAction] = useState(null)
  const [formType, setFormType] = useState(null)
  const [bookingDate, setBookingDate] = useState(null)
  const [selectedServices, setSelectedServices] = useState(null)
  const [bookingStartTime, setBookingStartTime] = useState('')
  const [bookingEndTime, setBookingEndTime] = useState('')
  const [defaultSlot, setDefaultSlot] = useState('')
  const [servicesDuration, setServicesDuration] = useState(0)
  const [disableCalcBtn, setDisableCalcBtn] = useState(true)
  const [availableSlot, setAvailableSlot] = useState([])
  const [isBranchClosed, setIsBranchClosed] = useState(false)
  const [statusChoices, setStatusChoices] = useState([])
  const [periods, setPeriods] = useState([])

  const { data: listBooking = [] } = useGetList(
    'booking',
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'create_at', order: 'ASC' },
      filter: {
        branch_code: selectedBranch,
        slot: selectedLane,
        start_date: moment(selectedDate).format('YYYY-MM-DD'),
        end_date: moment(selectedDate).format('YYYY-MM-DD'),
        statuses: ['reserve', 'pending', 'confirm', 'complete'],
      },
      meta: { isPaginate: false },
    },
    {
      enabled: !!selectedBranch && !!selectedLane && !!selectedDate,
    },
  )

  const { data: branchConfig = {}, isLoading: branchConfLoading } = useGetOne(
    'branch_config',
    {
      id: `${selectedBranch}/${moment(selectedDate).format('YYYY-MM-DD')}`,
    },
    {
      enabled: !!selectedBranch && !!selectedLane && !!selectedDate,
    },
  )

  const actionDialogOpen = (): void => setActionOpen(true)
  const actionDialogClose = (): void => {
    setActionOpen(false)
    events.pop()
    setEvents([...events])
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClose = (_event = null, reason = null): void => {
    if (reason && reason == 'backdropClick') return
    setSelectedEvent(null)
    setBookingOpen(false)
    setBookingDate(null)
    setStart(null)
    setEnd(null)
    setOverlap(false)
    setDefaultSlot('')
    setDisableCalcBtn(true)
    setPeriods([])
    if (!selectedEvent) events.pop()
    setEvents([...events])
  }
  const handleMouseUp = (): void => setOverlap(false)
  const findOverlappingTime = useCallback(
    (newStart, newEnd) => {
      for (const event of events.filter((e) => moment(e.start).isSame(moment(newStart), 'day'))) {
        const isStartOverlap = moment(event.start).isBetween(moment(newStart), moment(newEnd))
        const isEndOverlap = moment(event.end).isBetween(moment(newStart), moment(newEnd))
        if (isStartOverlap || isEndOverlap) {
          setOverlap(true)
          return true
        }
        const onStartEvent = moment(newStart).isBetween(
          moment(event.start),
          moment(event.end),
          undefined,
          '[)',
        )
        const onEndEvent = moment(newEnd).isBetween(
          moment(event.start),
          moment(event.end),
          undefined,
          '(]',
        )
        if (onStartEvent || onEndEvent) {
          setOverlap(true)
          return true
        }
      }
      return false
    },
    [events],
  )

  const getAvailableBooking = useCallback(async (): Promise<void> => {
    const res = await axios.get('booking/available', {
      params: {
        branch_code: selectedBranch,
        services_key: selectedServices,
        date: moment(bookingDate).format('YYYY-MM-DD'),
      },
    })
    const periods = _.get(res, 'data.result.dates[0].periods')
    if (periods.length < 1) {
      notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          สาขาถูกปิดในวันที่เลือก กรุณาเลือกวันที่อื่น
        </Alert>,
      )
    }
    setPeriods(periods)
  }, [bookingDate, notify, selectedBranch, selectedServices, t])
  useEffect(() => {
    setAvailableSlot(
      _.map(periods, (p) => {
        const id = timeToId(p)
        return {
          id: id,
          name: `${p.start_time} - ${p.end_time}${
            !p.is_available && id !== defaultSlot ? ' (เต็ม)' : ''
          }`,
          disabled: !p.is_available && id !== defaultSlot,
          start: p.start_time,
          end: p.end_time,
        }
      }),
    )
  }, [defaultSlot, periods])

  const handleSelectEvent = (e): void => {
    if (!bookingPermission?.updatable) {
      return notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          {t('ra.auth.booking_unupdatable')}
        </Alert>,
      )
    }
    if (overlap) {
      document.addEventListener('mouseup', handleMouseUp)
      return
    }
    // prevent edit backward
    // if (moment(e.start).endOf('day') < moment()) {
    //   return notify(
    //     <Alert severity='error'>
    //       <AlertTitle>{t('ra.page.error')}</AlertTitle>
    //       ไม่สามารถแก้ไขรายการจองของวันก่อนหน้าได้
    //     </Alert>,
    //   )
    // }
    const ev = _.find(listBooking, ['id', e.id])
    setSelectedEvent({
      ...ev,
      services_key: ev.selected_services_keys,
      booking_start_time: branchConfigTimeConverter(ev.booking_start_time),
      booking_end_time: branchConfigTimeConverter(ev.booking_end_time),
    })
    if (e.status === 'reserve') {
      setFormType('reserve')
    } else {
      setFormType('booking')
    }
    const pendingChoice = { id: 'pending', name: t(`${BOOKING_FIELDS}.status.choices.pending`) }
    const confirmChoice = { id: 'confirm', name: t(`${BOOKING_FIELDS}.status.choices.confirm`) }
    const cancelChoice = { id: 'cancel', name: t(`${BOOKING_FIELDS}.status.choices.cancel`) }
    const completeChoice = { id: 'complete', name: t(`${BOOKING_FIELDS}.status.choices.complete`) }
    let choices = []
    switch (ev?.status) {
      case 'pending':
        choices = [pendingChoice, confirmChoice]
        bookingPermission?.deletable ? choices.push(cancelChoice) : choices
        setStatusChoices(choices)
        break
      case 'confirm':
        choices = [completeChoice]
        bookingPermission?.deletable ? choices.push(cancelChoice) : choices
        setStatusChoices(choices)
        break
      case 'complete':
        setStatusChoices([completeChoice])
        break
      default:
        setStatusChoices([pendingChoice, confirmChoice])
        break
    }
    setBookingDate(new Date(ev?.booking_date))
    setSelectedServices(ev?.selected_services_keys)
    const slotId = timeToId({
      start_time: moment(branchConfigTimeConverter(ev?.booking_start_time)).format('HH:mm'),
      end_time: moment(branchConfigTimeConverter(ev?.booking_end_time)).format('HH:mm'),
    })
    setDefaultSlot(slotId)
    getAvailableBooking()
    setAvailableSlot([
      {
        id: slotId,
        name: `${moment(ev?.booking_start_time).format('HH:mm')}${' - '}
        ${moment(ev?.booking_end_time).format('HH:mm')}`,
      },
    ])
    setFormAction('update')
    setBookingOpen(true)
  }
  const handleSelectSlot = ({ start, end }): void => {
    if (!bookingPermission?.creatable) {
      return notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          {t('ra.auth.booking_uncreatable')}
        </Alert>,
      )
    }
    if (!selectedBranch || !selectedLane) {
      return notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          กรุณาเลือกสาขาและช่องจอดก่อน
        </Alert>,
      )
    }
    // prevent create backward
    if (moment(start) < moment().startOf('day')) {
      return notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          ไม่สามารถสร้างรายการจองย้อนหลังได้
        </Alert>,
      )
    }
    if (findOverlappingTime(start, end)) {
      return notify(
        <Alert severity='warning'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          เวลาที่เลือกมีรายการจองแล้ว กรุณาเลือกช่วงเวลาอื่น
        </Alert>,
      )
    }
    if (isBranchClosed) {
      return notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          สาขาถูกปิดในวันที่เลือก
        </Alert>,
      )
    }
    setEvents([
      ...events,
      {
        id: 'pre-create',
        title: '',
        start,
        end,
        status: null,
      },
    ])
    setStatusChoices([
      { id: 'pending', name: t(`${BOOKING_FIELDS}.status.choices.pending`) },
      { id: 'confirm', name: t(`${BOOKING_FIELDS}.status.choices.confirm`) },
    ])
    setFormAction('create')
    setBookingDate(new Date(start))
    setStart(new Date(start))
    setEnd(new Date(end))
    actionDialogOpen()
  }
  const handleNavigate = (d): void => {
    setSelectDate(d)
  }
  const onError = useCallback(
    (error): void => {
      const errorMessage = error.response.data.message
      if (error.response.data.code === 'FORBIDDEN') {
        return notify(
          'ra.auth.access_denied' ||
            (formAction === 'create'
              ? 'ra.validation.create_booking_fail'
              : 'ra.validation.update_booking_fail'),
          {
            type: 'error',
          },
        )
      }
      if (error.response.data.code === '50001') {
        return notify(
          <Alert severity='error'>
            <AlertTitle>{t('ra.page.error')}</AlertTitle>
            สาขาถูกปิดในวันที่เลือก กรุณาเลือกวันที่อื่น
          </Alert>,
        )
      }
      if (error.response.data.code === '50002') {
        return notify(
          <Alert severity='error'>
            <AlertTitle>{t('ra.page.error')}</AlertTitle>
            เวลาที่เข้ารับบริการไม่ถูกต้อง กรุณาตรวจสอบใหม่อีกครั้ง
          </Alert>,
        )
      }
      if (error.response.data.code === '50003') {
        return notify(
          <Alert severity='error'>
            <AlertTitle>{t('ra.page.error')}</AlertTitle>
            ไม่มีช่วงเวลาที่ว่างในวันที่เลือก
            <br />
            กรุณาเปลี่ยนวันที่และเลือกช่วงเวลาที่จะเข้ารับบริการอีกครั้ง
          </Alert>,
        )
      }
      return notify(errorMessage || 'Booking create failed', { type: 'error' })
    },
    [formAction, notify, t],
  )
  const onSuccessCreate = useCallback((): void => {
    notify(
      <Alert severity='success'>
        <AlertTitle>สำเร็จ!</AlertTitle>
        Booking created
      </Alert>,
    )
    setBookingOpen(false)
    setTimeout((): void => {
      window.open(`/booking?date=${moment(selectedDate).format('YYYY-MM-DD')}`, '_self')
    }, 800)
  }, [notify, selectedDate])
  const onSuccessUpdate = useCallback((): void => {
    notify(
      <Alert severity='success'>
        <AlertTitle>สำเร็จ!</AlertTitle>
        Booking updated
      </Alert>,
    )
    setBookingOpen(false)
    setTimeout((): void => {
      window.open(`/booking?date=${moment(selectedDate).format('YYYY-MM-DD')}`, '_self')
    }, 800)
  }, [notify, selectedDate])
  const [create] = useCreate(undefined, undefined, { onError, onSuccess: onSuccessCreate })
  const [update] = useUpdate(undefined, undefined, { onError, onSuccess: onSuccessUpdate })

  const handleSubmit = (data): void => {
    if (data.formType === 'reserve') {
      const reserveData = {
        branch_code: selectedBranch,
        slot: data.lane,
        booking_date: moment(selectedDate).format('YYYY-MM-DD'),
        booking_start_time: moment(data.booking_start_time).format('HH:mm'),
        booking_end_time: moment(data.booking_end_time).format('HH:mm'),
        more_detail: data.more_detail,
      }
      if (data.formAction === 'create') {
        create('reserve', { data: reserveData })
      } else {
        update('reserve', { id: data.id, data: { ...reserveData, status: 'cancel' } })
      }
    } else {
      const bookingData = {
        branch_code: selectedBranch,
        booking_date: moment(data.booking_date).format('YYYY-MM-DD'),
        booking_start_time: bookingStartTime
          ? bookingStartTime
          : moment(data.booking_start_time).format('HH:mm'),
        booking_end_time: bookingEndTime
          ? bookingEndTime
          : moment(data.booking_end_time).format('HH:mm'),
        ...(data.formAction === 'update' && {
          cancel_reason:
            data.cancel_reason_choices === 'other'
              ? data.cancel_reason
              : data.cancel_reason_choices === 'out_of_stock'
              ? 'สินค้าขาดตลาด'
              : data.cancel_reason_choices === 'inconvenient'
              ? 'ลูกค้าไม่สะดวกเข้ารับบริการ'
              : null,
        }),
        first_name: data?.first_name ?? selectedEvent?.first_name,
        last_name: data?.last_name ?? selectedEvent?.last_name,
        email: data?.email ?? selectedEvent?.email,
        tel: data?.tel ?? selectedEvent?.tel,
        license_plate: data?.license_plate ?? selectedEvent?.license_plate,
        province_id: data?.province_id ?? selectedEvent?.province_id,
        is_accept_terms_conditions: data?.is_accept_terms_conditions ?? true,
        car_brand: data?.car_brand ?? selectedEvent?.car_brand,
        car_model: data?.car_model ?? selectedEvent?.car_model,
        more_detail: data?.more_detail ?? selectedEvent?.more_detail,
        status: data?.status,
        services_key: data?.services_key ?? selectedEvent?.services_key,
      }
      if (moment(data.booking_date).isBefore(moment(), 'day')) {
        return notify(
          <Alert severity='error'>
            <AlertTitle>{t('ra.page.error')}</AlertTitle>
            ไม่สามารถบันทึกรายการจองด้วยวันที่ย้อนหลังได้
          </Alert>,
        )
      }
      if (data.formAction === 'create') {
        create('booking', { data: bookingData })
      } else {
        update('booking', { id: data.id, data: bookingData })
      }
    }
  }

  // useEffect(() => {
  //   if (!selectedBranch || !date || !selectedServices || selectedServices.length < 1) {
  //     setDisableCalcBtn(true)
  //   } else {
  //     setDisableCalcBtn(false)
  //   }
  // }, [date, selectedBranch, selectedServices])

  useEffect(() => {
    setIsBranchClosed(branchConfig?.open_slot < 1)
    if (selectedLane > branchConfig?.open_slot) {
      setSelectLane(1)
    }
  }, [branchConfig?.open_slot, selectedLane])
  useEffect(() => {
    const bookingEvents = listBooking.map((e) => {
      const openTimeStr = branchConfigTimeConverter(e.booking_start_time)
      const closeTimeStr = branchConfigTimeConverter(e.booking_end_time)
      const applyDate = moment(e.booking_date)
      const openTime = moment(openTimeStr, 'HH:mm')
      const closeTime = moment(closeTimeStr, 'HH:mm')
      const start = moment(applyDate).set({
        hour: openTime.get('hour'),
        minute: openTime.get('minute'),
        second: openTime.get('second'),
      })
      const end = moment(applyDate).set({
        hour: closeTime.get('hour'),
        minute: closeTime.get('minute'),
        second: closeTime.get('second'),
      })
      return {
        id: e.id,
        title: `Name: ${e.first_name} ${e.last_name}\nTel: ${e.tel}\nE-mail: ${e.email}`,
        start: start.toDate(),
        end: end.toDate(),
        bgColor:
          e.status == 'complete'
            ? STATUS_COLOR.COMPLETE
            : e.status == 'confirm'
            ? STATUS_COLOR.CONFIRM
            : e.status == 'pending'
            ? STATUS_COLOR.PENDING
            : STATUS_COLOR.RESERVE,
        name: `${e.first_name} ${e.last_name}`,
        tel: e.tel,
        email: e.email,
        status: e.status,
      }
    })
    setEvents(bookingEvents)
  }, [listBooking])

  const Event = ({ event }): JSX.Element => {
    return event.id === 'pre-create' ? (
      <></>
    ) : (
      <div style={{ lineHeight: 1.7 }}>
        สถานะ : {t(`${BOOKING_FIELDS}.status.choices.${event.status}`)}
        {event.status !== 'reserve' && ` | ${event.name} | ${event.tel} | ${event.email}`}
      </div>
    )
  }
  const isBookingConfirmOrComplete = useMemo(
    () => selectedEvent?.status === 'confirm' || selectedEvent?.status === 'complete',
    [selectedEvent?.status],
  )
  const isBookingComplete = useMemo(
    () => selectedEvent?.status === 'complete',
    [selectedEvent?.status],
  )

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const availableSlotValidate = (value) => {
    if (!value || value?.length < 1) {
      return 'กรุณากรอกข้อมูล'
    }
    return undefined
  }
  const SelectBranch = useCallback((): JSX.Element => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { setValue } = useFormContext()
    return (
      <BookingSelectBranch
        label={t('component.searchBar.placeholder')}
        value={selectedBranch}
        onChange={(e): void => {
          setAvailableSlot([])
          setSelectedBranch(e.target.value)
          setSelectedServices([])
          setValue('services_key', [])
          setServicesDuration(0)
          setDisableCalcBtn(true)
        }}
        disabled={formType === 'reserve' || isBookingConfirmOrComplete}
        excludeUnconfigured
      />
    )
  }, [formType, isBookingConfirmOrComplete, selectedBranch, t])

  const BookingDateInput = useCallback((): JSX.Element => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { setValue } = useFormContext()
    return (
      <DateInput
        source='booking_date'
        label={`${BOOKING_FIELDS}.booking_date`}
        defaultValue={bookingDate}
        onChange={(e): void => {
          setBookingDate(e.target.value)
          setAvailableSlot([])
          setValue('available_slot', [])
          setDisableCalcBtn(false)
        }}
        validate={[required()]}
        disabled={isBookingConfirmOrComplete}
        fullWidth
      />
    )
  }, [bookingDate, isBookingConfirmOrComplete])

  useEffect(() => {
    setBookingPermission(bookingBookManagementPermission)
  }, [bookingBookManagementPermission])

  if (isUserLoading && !bookingPermission) {
    return <Loading />
  }
  if (errorUser) {
    return (
      <Alert severity='error'>
        <AlertTitle>{t('ra.page.error')}</AlertTitle>
        User Error : {errorUser.message}
      </Alert>
    )
  }

  return bookingPermission && !bookingPermission?.readable ? (
    <Alert severity='error'>
      <AlertTitle>{t('ra.page.error')}</AlertTitle>
      {t('ra.auth.access_denied')}
    </Alert>
  ) : (
    <BasePageLayout title='Booking'>
      <Grid container>
        <Grid item xs={12}>
          <BookingSelectBranch
            label={t('component.searchBar.placeholder')}
            value={selectedBranch}
            onChange={(e): void => setSelectedBranch(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <LaneSelectButton
            options={branchConfig?.open_slot}
            value={selectedLane}
            onChange={(e): void => setSelectLane(e)}
            loading={branchConfLoading}
          />
        </Grid>
      </Grid>
      {/* Calendar */}
      <Calendar
        date={selectedDate}
        components={{
          event: Event,
        }}
        events={events}
        view={view}
        views={['day']}
        onView={(v): void => setView(v)}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onNavigate={handleNavigate}
        step={15}
      ></Calendar>
      {/* Action */}
      <Dialog open={actionOpen} fullWidth={true} maxWidth='sm' onClose={actionDialogClose}>
        <DialogTitle
          id='action-dialog-title'
          sx={{ typography: 'h5', textAlign: 'center', mt: '0.5rem' }}
        >
          คุณต้องการสร้างรายการประเภทใด?
        </DialogTitle>
        <DialogContent sx={{ mt: '0.5rem' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Button
                variant='contained'
                size='large'
                onClick={(): void => {
                  setFormType('reserve')
                  setBookingOpen(true)
                  setActionOpen(false)
                }}
                fullWidth
              >
                Create Reserve
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant='contained'
                size='large'
                onClick={(): void => {
                  setFormType('booking')
                  setBookingOpen(true)
                  setActionOpen(false)
                }}
                fullWidth
              >
                Create Booking
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
      {/* Reserve & Booking */}
      <Dialog
        fullWidth={false}
        maxWidth='sm'
        open={bookingOpen}
        onClose={handleClose}
        PaperProps={{ sx: { ml: 'auto' } }}
      >
        <Grid container>
          <Grid item xs={10}>
            <DialogTitle sx={{ typography: 'h4' }}>
              {!selectedEvent ? 'Create' : 'Edit'} {_.startCase(_.toLower(formType))}
            </DialogTitle>
          </Grid>
          <Grid item xs={2}>
            <DialogActions>
              <Button endIcon={<CloseIcon />} onClick={handleClose}>
                {t('ra.action.close')}
              </Button>
            </DialogActions>
          </Grid>
        </Grid>
        <DialogContent sx={{ pt: 0 }}>
          <DialogContentText>กำหนดค่าการจอง</DialogContentText>
          <SimpleForm
            record={
              selectedEvent
                ? {
                    ...selectedEvent,
                    formType,
                    formAction: 'update',
                  }
                : { ...record, formType, formAction: 'create' }
            }
            toolbar={
              <Toolbar>
                {!!selectedEvent && selectedEvent?.status === 'reserve' ? (
                  <SaveButton
                    label={t('ra.action.cancel')}
                    color='error'
                    variant='contained'
                    icon={<CancelIcon />}
                    alwaysEnable={true}
                    sx={{ ml: 'auto' }}
                  />
                ) : (
                  <SaveButton sx={{ ml: 'auto' }} />
                )}
              </Toolbar>
            }
            onSubmit={handleSubmit}
            sx={{ pt: '40px' }}
          >
            <Box
              noValidate
              component='form'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                m: 'auto',
                width: 'fit-content',
              }}
            >
              <Grid container item spacing={1} xs={12}>
                <Grid item xs={12}>
                  <SelectBranch />
                </Grid>
                {formType === 'reserve' && (
                  <>
                    <Grid item xs={12}>
                      <NumberInput
                        source='lane'
                        label={`${BOOKING_FIELDS}.booking_slot`}
                        defaultValue={selectedLane}
                        disabled
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TimeInput
                        source='booking_start_time'
                        label={`${BOOKING_FIELDS}.start_time`}
                        defaultValue={start}
                        disabled
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TimeInput
                        source='booking_end_time'
                        label={`${BOOKING_FIELDS}.end_time`}
                        defaultValue={end}
                        disabled
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                {formType === 'booking' && (
                  <>
                    <Grid item xs={12}>
                      <BookingDateInput />
                    </Grid>
                    <Grid item xs={12}>
                      <ServiceMultiSelectInput
                        source='services_key'
                        label={`${BOOKING_FIELDS}.service`}
                        value={selectedEvent?.services_key}
                        allServicesButton={false}
                        filterByBranchCode={selectedBranch}
                        updateTotalDuration={setServicesDuration}
                        setServices={(s): void => {
                          setAvailableSlot([])
                          setSelectedServices(s)
                          setDisableCalcBtn(false)
                        }}
                        validate={[required()]}
                        disabled={isBookingConfirmOrComplete}
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      container
                      sx={{ pl: '8px' }}
                      alignItems={'center'}
                      justifyContent='space-between'
                    >
                      <Grid item>
                        <Typography variant='body1'>
                          รวมเวลาบริการทั้งหมด : {servicesDuration} นาที
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Button
                          variant='contained'
                          size='medium'
                          onClick={getAvailableBooking}
                          sx={{ width: 'fit-content' }}
                          disabled={disableCalcBtn}
                        >
                          คำนวณเวลาใหม่
                        </Button>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: '10px' }}>
                      <SelectInput
                        source='available_slot'
                        label='เลือกเวลาที่เข้ารับบริการ'
                        choices={availableSlot}
                        defaultValue={defaultSlot}
                        disabled={availableSlot.length < 1 || isBookingConfirmOrComplete}
                        onChange={(e): void => {
                          setBookingStartTime(
                            _.find(availableSlot, ['id', e.target.value])['start'],
                          )
                          setBookingEndTime(_.find(availableSlot, ['id', e.target.value])['end'])
                        }}
                        validate={availableSlotValidate}
                        fullWidth
                        sx={{ display: disableCalcBtn === false ? 'block' : 'none' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {formAction === 'update' && disableCalcBtn === true ? (
                        <FunctionField
                          render={(record): JSX.Element => {
                            const startTime = moment(record.booking_start_time, 'HH:mm').format(
                              'HH:mm',
                            )
                            const endTime = moment(record.booking_end_time, 'HH:mm').format('HH:mm')
                            return (
                              <Typography variant='body1' mb={2}>
                                เวลาจอง : {`${startTime} - ${endTime}`}
                              </Typography>
                            )
                          }}
                        />
                      ) : (
                        ''
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        source='first_name'
                        label={`${BOOKING_FIELDS}.first_name`}
                        disabled={isBookingConfirmOrComplete}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        source='last_name'
                        label={`${BOOKING_FIELDS}.last_name`}
                        disabled={isBookingConfirmOrComplete}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        source='email'
                        label={`${BOOKING_FIELDS}.email`}
                        disabled={isBookingConfirmOrComplete}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        source='tel'
                        label={`${BOOKING_FIELDS}.tel`}
                        disabled={isBookingConfirmOrComplete}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        source='license_plate'
                        label={`${BOOKING_FIELDS}.license_plate`}
                        disabled={isBookingConfirmOrComplete}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    {/* Province field removed — จังหวัด no longer collected (backend province_id now optional). Ref: Hide Province task tk_8ihzaxo */}
                    <Grid item xs={6}>
                      <TextInput
                        source='car_brand'
                        label={`${BOOKING_FIELDS}.car_brand`}
                        disabled={isBookingConfirmOrComplete}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        source='car_model'
                        label={`${BOOKING_FIELDS}.car_model`}
                        disabled={isBookingConfirmOrComplete}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextInput
                    source='more_detail'
                    label={`${BOOKING_FIELDS}.more_detail`}
                    multiline
                    rows={4}
                    disabled={
                      isBookingConfirmOrComplete ||
                      (formType === 'reserve' && selectedEvent?.status === 'reserve')
                    }
                    fullWidth
                  />
                </Grid>
                {formType === 'booking' && (
                  <Grid item xs={12}>
                    <SelectInput
                      source='status'
                      label={`${BOOKING_FIELDS}.status.label`}
                      choices={statusChoices}
                      validate={[required()]}
                      disabled={isBookingComplete}
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <CancelReasonInput />
                </Grid>
              </Grid>
            </Box>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </BasePageLayout>
  )
}

export default BookingResource
